import React, { useState, useEffect } from "react";
import "../styles/OTPDialog.css";

const OTPDialog = ({ isOpen, onClose, onVerify, onResend, email }) => {
  const [otp, setOtp] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [countdown, setCountdown] = useState(90); // 1.5 minutes in seconds

  useEffect(() => {
    if (!isOpen) {
      // Reset state when dialog closes
      setOtp("");
      setError("");
      setSuccessMessage("");
      setCountdown(90);
      return;
    }

    // Countdown timer
    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [isOpen]);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (otp.length !== 6) {
      setError("Please enter a 6-digit OTP");
      return;
    }

    setLoading(true);
    setError("");
    setSuccessMessage("");

    try {
      await onVerify(otp);
    } catch (err) {
      setError(err.response?.data?.message || err.message || "Invalid OTP");
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    setResendLoading(true);
    setError("");
    setSuccessMessage("");

    try {
      await onResend();
      setCountdown(90); // Reset countdown
      setOtp(""); // Clear OTP input
      setSuccessMessage("OTP resent successfully! Check your email.");
    } catch (err) {
      setError(err.response?.data?.message || "Failed to resend OTP");
    } finally {
      setResendLoading(false);
      const timer = setInterval(() => {
        setCountdown((prev) => {
          if (prev <= 1) {
            clearInterval(timer);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      return () => clearInterval(timer);
    }
  };

  const handleOtpChange = (e) => {
    const value = e.target.value.replace(/\D/g, "").slice(0, 6);
    setOtp(value);
    setError(""); // Clear error on input change
  };

  if (!isOpen) return null;

  return (
    <div className="otp-dialog-overlay" onClick={onClose}>
      <div className="otp-dialog" onClick={(e) => e.stopPropagation()}>
        <div className="otp-dialog-header">
          <h2>Enter Verification Code</h2>
          <button className="close-icon" onClick={onClose} aria-label="Close">
            ×
          </button>
        </div>

        <div className="otp-dialog-body">
          <p className="otp-info">We've sent a 6-digit verification code to</p>
          <p className="otp-email">{email}</p>

          <form onSubmit={handleSubmit}>
            <div className="otp-input-container">
              <input
                type="text"
                value={otp}
                onChange={handleOtpChange}
                placeholder="000000"
                maxLength="6"
                className="otp-input"
                autoFocus
                disabled={loading}
              />
            </div>

            {error && (
              <div className="otp-error">
                <span className="error-icon">⚠</span>
                {error}
              </div>
            )}

            {successMessage && countdown > 85 && (
              <div className="otp-success">
                <span className="success-icon">✓</span>
                {successMessage}
              </div>
            )}

            <button
              type="submit"
              className="otp-verify-btn"
              disabled={loading || otp.length !== 6 || countdown === 0}
            >
              {loading ? (
                <>
                  <span className="spinner"></span>
                  Verifying...
                </>
              ) : (
                "Verify OTP"
              )}
            </button>
          </form>

          <div className="otp-resend-section">
            <p>Didn't receive the code?</p>
            <button
              onClick={handleResend}
              className="otp-resend-btn"
              disabled={resendLoading || countdown > 0}
            >
              {resendLoading
                ? "Sending..."
                : countdown > 0
                ? "Resend OTP in " + formatTime(countdown)
                : "Resend OTP"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OTPDialog;
